import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ObscuredData } from '@se/components';
import { SeConstraintConfiguratorService } from '@se/constraint';
import { HardcodedData, isJsonObject, Json, JsonObject } from '@se/cp.common';
import {
    Addendum,
    AuthenticationService,
    ButtonsService,
    CancellationError,
    CpHttpSaveOptions,
    CpHttpService,
    EstensaWaitingService,
    IPatientReportPair,
    IReport,
    isMergeFault,
    KeyImage,
    MeasureImportRequest,
    PacsTuple,
    RadioSettingsService,
    ReportingDataBuilder,
    SrImportRequest,
    XmlRequestType,
} from '@se/platform';
import { tapAlways, DataBinding, Logger } from '@se/se.ui.html5';
import { ButtonsTypes, ObscuredTabType } from 'estensa-module';
import { differenceWith, every, some } from 'lodash-es';
import moment, { isMoment, Moment } from 'moment';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    finalize,
    firstValueFrom,
    from,
    map,
    Observable,
    of,
    switchMap,
    take,
    throwError,
} from 'rxjs';

import * as fromCp from './store/cp.state';
import { CpEntity, CpEntityList, ICpEntity, isEntity, PropertyType } from './cp-configuration/view-model';
import { CpModelService } from './cp-model.service';
import { selectCpModules, selectCpPage } from './store/cp.selectors';
import { CpDatasourceService } from './cp-datasource.service';
import { isNotUndefined } from './cp-configuration/utils/utils';
import { cpApplyNormalValues } from './store/cp.actions';

export interface ReportStatus {
    isAdmitted: boolean;
    isApproved: boolean;
    isDigitallySigned: boolean;
    isExecuted: boolean;
    isReported: boolean;
}

export function isReportStatusEqual(a: ReportStatus, b: ReportStatus) {
    return (
        a.isAdmitted === b.isAdmitted &&
        a.isApproved === b.isApproved &&
        a.isDigitallySigned === b.isDigitallySigned &&
        a.isExecuted === b.isExecuted &&
        a.isReported === b.isReported
    );
}

export interface ApproveOptions {
    addendum?: Addendum;
    referringPhysician?: any; // !!!! FIXME any
}

export class PartialError extends Error {
    constructor(
        readonly newModel: Json,
        error: Error,
    ) {
        super(error.message);
    }
}

export interface SignOptions {
    print?: boolean;
    addendum?: Addendum;
    referringPhysician?: any; // !!!! FIXME any
}

export interface IReferringPhysician {
    Id: string;
    Description: string;
    Order: number;
    Path: string;
    Code: string | null;
    IdPrefix: string;
    IdRole: string;
    EnableDigitalSign: boolean;
    IdDefaultFunctionality: string;
    UserName: string;
    SignatoryCode: string | null;
    RoleOnReport: string | null;
    IsReviewer: boolean;
    IsPostgraduate: boolean;
    ExternalCode: string | null;
    CanBeAssignedAsReader: boolean;
    EmployeeSerialNumber: string;
    Surname: string;
    Name: string;
}

@Injectable({
    providedIn: 'root',
})
export class CpReportService {
    constructor(
        private authenticationService: AuthenticationService,
        private buttonsService: ButtonsService,
        private configuratorService: SeConstraintConfiguratorService,
        private cpHttpService: CpHttpService,
        private dataSourceService: CpDatasourceService,
        private logger: Logger,
        private modelService: CpModelService,
        private radioSettingsService: RadioSettingsService,
        private reportingDataBuilder: ReportingDataBuilder,
        private store: Store<fromCp.RootState>,
        private waitingService: EstensaWaitingService,
    ) {}

    // #region Model Getters

    get modelTypeRootIds() {
        return this.modelService.getValue(HardcodedData.bindings.idModelTypeRootList) as string[];
    }

    get examinations$() {
        return this.modelService.registerList(HardcodedData.bindings.examinations);
    }

    get examinations() {
        return this.modelService.getEntities(HardcodedData.bindings.examinations);
    }

    get examinationIds$() {
        return this.examinations$.pipe(map((exs) => exs?.activeEntities.map((ex) => ex.Id) ?? []));
    }

    get examinationIds() {
        return this.examinations?.map((ex) => ex.Id);
    }

    get examId() {
        return this.modelService.getValue(HardcodedData.bindings.examId);
    }

    get exam() {
        return this.modelService.getEntity(HardcodedData.bindings.exam);
    }

    get exam$() {
        return this.modelService.register(HardcodedData.bindings.exam).pipe(filter(isEntity));
    }

    get referenceExamination() {
        return this.modelService.getEntity(HardcodedData.bindings.referenceExamination);
    }

    get isDigitallySigned() {
        return this.modelService.getValue(HardcodedData.bindings.isDigitallySigned);
    }

    get referenceExamination$() {
        return this.modelService.register(HardcodedData.bindings.referenceExamination).pipe(filter(isEntity));
    }

    get referenceExaminationId() {
        return this.referenceExamination?.Id;
    }

    get referenceExaminationId$() {
        return this.referenceExamination$.pipe(map((ex) => ex?.Id));
    }

    get referringPhysicianId() {
        return (this.modelService.getValue(HardcodedData.bindings.referringPhysician) as any)?.Id;
    }

    get reportId() {
        return this.modelService.getEntity(HardcodedData.bindings.report)?.Id;
    }

    get patientId() {
        return this.modelService.getEntity(HardcodedData.bindings.patient)?.Id;
    }

    get addendums() {
        return this.modelService.getEntities(HardcodedData.bindings.addendums);
    }

    get bsa() {
        const value = this.referenceExamination?.getPropertyValue('Bsa');
        if (!value || typeof value !== 'number') {
            return 0;
        }
        return value;
    }

    get examDate(): Moment | undefined {
        const referenceExamination = this.referenceExamination;
        if (!referenceExamination) {
            return;
        }

        const examDate =
            referenceExamination.getPropertyValue('ReportedDate') ??
            referenceExamination.getPropertyValue('ExecutionDate') ??
            referenceExamination.getPropertyValue('AdmissionDate');

        if (!examDate) {
            return;
        }

        if (isMoment(examDate)) {
            return examDate;
        }

        return moment(examDate.toString());
    }

    get isValid$() {
        return this.modelService.getModelAndValidity().pipe(map(([_, isValid]) => isValid));
    }

    get canApplyNormalValues$() {
        // TODO: Togliere da qui.
        const clinicalEvaluations = this.store.pipe(
            select(selectCpModules),
            switchMap((modules) => {
                if (modules.includes('Us')) {
                    return this.modelService.registerList(
                        HardcodedData.bindings.ecoCv.clinicalEvaluationsComboIndexed,
                        false,
                    );
                }
                return of(undefined);
            }),
        );

        return combineLatest([this.examinations$, clinicalEvaluations]).pipe(
            map(([exList, ceList]) => {
                const examinations = exList?.activeEntities;
                if (this.isApproved(examinations ?? [])) {
                    return false;
                }

                const clinicalEvaluations = ceList?.activeEntities;

                return (
                    (clinicalEvaluations?.length ?? 0) > 0 ||
                    this.configuratorService.normalValuesConstraints.length !== 0
                );
            }),
            distinctUntilChanged(),
        );
    }

    get canApprove$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                const addendums = this.getAddendums(model);

                return (
                    !!examinations &&
                    examinations.length > 0 &&
                    ((isValid && this.isApprovable(examinations)) || this.isInAddendumAnomalyStatus(addendums))
                );
            }),
            distinctUntilChanged(),
        );
    }

    get canUndoValidation$() {
        return this.examinations$.pipe(
            map((model) => {
                if (this.radioSettingsService.isExternalSigningEnabled() && !this.isDigitallySigned) {
                    return false;
                }

                const examinations = model?.activeEntities;
                return (
                    !!examinations &&
                    examinations.length > 0 &&
                    this.isValidationUndoable(examinations) &&
                    (!this.addendums || this.addendums.length == 0)
                );
            }),
            distinctUntilChanged(),
        );
    }

    get canSign$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                const addendums = this.getAddendums(model);

                return (
                    !!examinations &&
                    examinations.length > 0 &&
                    ((isValid && this.isSignable(examinations)) || this.isInAddendumAnomalyStatus(addendums))
                );
            }),
            distinctUntilChanged(),
        );
    }

    get canAttachedDocs$() {
        return this.modelService.registerRoot().pipe(
            map((root) => !!root),
            distinctUntilChanged(),
        );
    }

    get canDeleteExamination$() {
        return this.examinations$.pipe(
            map((exs) => {
                if (!exs) {
                    return false;
                }
                if (this.isApproved(exs.activeEntities)) {
                    return false;
                }
                if (this.isReported(exs.activeEntities)) {
                    return this.buttonsService.isButtonTypeAuthorized(ButtonsTypes.deleteReport);
                }
                return true;
            }),
        );
    }

    get canExecute$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                return !!examinations && examinations.length > 0 && isValid && this.isExecutable(examinations);
            }),
            distinctUntilChanged(),
        );
    }

    get canExternalLinks$() {
        return this.modelService.registerRoot().pipe(
            map((root) => !!root),
            distinctUntilChanged(),
        );
    }

    get canImages$() {
        return this.examinations$.pipe(
            map((exs) => exs?.activeEntities.some((ex) => !!ex.getPropertyValue('ImageAvailability')) ?? false),
            distinctUntilChanged(),
        );
    }

    get canImport$() {
        return this.examinations$.pipe(
            map((model) => {
                const examinations = model?.activeEntities;
                return !this.isApproved(examinations ?? []);
            }),
            distinctUntilChanged(),
        );
    }

    get canObscuration$() {
        const obscuredTabType = this.radioSettingsService.radioSettings.reportConfiguration.obscuredTabType;
        const isExternalSigningApp = this.radioSettingsService.isExternalSigningEnabled();
        return this.examinations$.pipe(
            map((model) => {
                const examinations = model?.activeEntities;
                if (!examinations || examinations.length === 0) {
                    return false;
                }
                const isApproved = this.isApproved(examinations);

                /*vedi schema bug 95304*/
                if (isExternalSigningApp && this.isReported(examinations) && !this.isDigitallySigned && !isApproved) {
                    return true;
                }
                /*92337*/
                if (isExternalSigningApp && !this.isDigitallySigned) {
                    return false;
                }

                return (
                    !!obscuredTabType &&
                    (!isApproved ||
                        // Per FSE2.0 si può modificare l'oscuramento anche dopo la resa in definitivo.
                        obscuredTabType === ObscuredTabType.FSE20)
                );
            }),
            distinctUntilChanged(),
        );
    }

    get canCreateMedicalPresciption$() {
        let canCreateMedicalPrescription = this.radioSettingsService.radioSettings.btnCardioCreateMedicalPrescription;
        return this.modelService.registerEntity(HardcodedData.bindings.patient).pipe(
            map((patientData) => {
                const isLocalPatient = patientData?.getPropertyValue('IsLocalPatient');
                return canCreateMedicalPrescription && patientData !== undefined && !isLocalPatient;
            }),
            distinctUntilChanged(),
        );
    }

    get canPatient$() {
        return this.modelService.registerEntity(HardcodedData.bindings.patient).pipe(
            map((patientData) => {
                const isLocalPatient = patientData?.getPropertyValue('IsLocalPatient');
                return patientData !== undefined && !isLocalPatient;
            }),
            distinctUntilChanged(),
        );
    }

    get canPrintPreview$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                return !!examinations && examinations.length > 0 && isValid && this.isReported(examinations);
            }),
            distinctUntilChanged(),
        );
    }

    get canPrintCertificate$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                return !!examinations && examinations.length > 0 && isValid && this.isApproved(examinations);
            }),
            distinctUntilChanged(),
        );
    }

    get canBurn$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                return (
                    !!examinations &&
                    examinations.length > 0 &&
                    isValid &&
                    (this.isApproved(examinations) || this.isSigned(examinations))
                );
            }),
            distinctUntilChanged(),
        );
    }

    getCollaborationTeamsExams() {
        const selectedReports = this.modelService.getAsJson().Report;
        return selectedReports?.Examinations.map((exam: any) => {
            return { id: exam.Id };
        });
    }

    get canSave() {
        const [model, isValid] = this.modelService.getModelAndValiditySync();
        const examinations = this.getExaminations(model);
        return (
            !!examinations &&
            examinations.length > 0 &&
            !this.isApproved(examinations) &&
            (!this.isReported(examinations) || (this.isReported(examinations) && isValid))
        );
    }

    get canSave$() {
        return this.modelService.getModelAndValidity().pipe(
            map(([model, isValid]) => {
                const examinations = this.getExaminations(model);
                return (
                    !!examinations &&
                    examinations.length > 0 &&
                    !this.isApproved(examinations) &&
                    (!this.isReported(examinations) || (this.isReported(examinations) && isValid))
                );
            }),
            distinctUntilChanged(),
        );
    }

    get canVisitType$() {
        return this.examinations$.pipe(
            map((model) => {
                const examinations = model?.activeEntities;
                return !!examinations && examinations.length > 0 && !this.isApproved(examinations);
            }),
            distinctUntilChanged(),
        );
    }

    get keyImages(): KeyImage[] {
        let entity = this.modelService.getEntity(HardcodedData.bindings.keyImages, false);
        if (!entity || !(entity instanceof CpEntityList)) {
            return [];
        }

        return entity.activeEntities.map((e) => this.keyImageFromEntity(e));
    }

    get keyImages$(): Observable<KeyImage[]> {
        return this.modelService
            .registerList(HardcodedData.bindings.keyImages, true, false)
            .pipe(map((list) => list?.activeEntities.map((e) => this.keyImageFromEntity(e)) ?? []));
    }

    get imagesForRowInPlaceholder() {
        return (this.exam?.getPropertyValue('ImagePerRowInPlaceholder') ?? 0) as number;
    }

    get isModelReadonly$() {
        return this.referenceExamination$.pipe(
            map((examination) => {
                if (!examination) {
                    return false;
                }

                return (
                    !!examination.getPropertyValue('IsApproved') || !!examination.getPropertyValue('IsDigitallySigned')
                );
            }),
        );
    }

    get reportStatus$(): Observable<ReportStatus> {
        return this.examinations$.pipe(
            map((exs) => exs?.activeEntities),
            filter(isNotUndefined),
            map((exs) => ({
                isAdmitted: exs.some((ex) => ex.getPropertyValue('IsAdmitted')),
                isApproved: exs.some((ex) => ex.getPropertyValue('IsApproved')),
                isDigitallySigned: exs.some((ex) => ex.getPropertyValue('IsDigitallySigned')),
                isExecuted: exs.some((ex) => ex.getPropertyValue('IsExecuted')),
                isReported: exs.some((ex) => ex.getPropertyValue('IsReported')),
            })),
            distinctUntilChanged(isReportStatusEqual),
        );
    }

    get referenceExaminationStatus$(): Observable<ReportStatus> {
        return this.referenceExamination$.pipe(
            filter(isNotUndefined),
            map((ex) => ({
                isAdmitted: !!ex.getPropertyValue('IsAdmitted'),
                isApproved: !!ex.getPropertyValue('IsApproved'),
                isDigitallySigned: !!ex.getPropertyValue('IsDigitallySigned'),
                isExecuted: !!ex.getPropertyValue('IsExecuted'),
                isReported: !!ex.getPropertyValue('IsReported'),
            })),
            distinctUntilChanged(isReportStatusEqual),
        );
    }

    get measureSourceUS2AI(): ICpEntity | null {
        const measureSourcesList = this.modelService.getEntities(HardcodedData.bindings.ecoCv.measureSource) ?? [];
        const source = measureSourcesList.find((entity) => {
            const lsSource = entity.getPropertyValue('IdUsMeasureSource');

            if (!isEntity(lsSource)) return lsSource === HardcodedData.idUS2AIMeasureSource;
            return lsSource.getPropertyPrimitiveValue('Id') === HardcodedData.idUS2AIMeasureSource;
        });
        return source ?? null;
    }

    get measureSourceUS2AI$(): Observable<ICpEntity | null> {
        const idUS2AIMeasureSource = HardcodedData.idUS2AIMeasureSource;
        return this.modelService.registerList(HardcodedData.bindings.ecoCv.measureSource).pipe(
            map((measureSources) => {
                const source = measureSources?.activeEntities?.find((entity) => {
                    const lsSource = entity.getPropertyValue('IdUsMeasureSource');
                    if (!isEntity(lsSource)) return lsSource === idUS2AIMeasureSource;
                    return lsSource.getPropertyPrimitiveValue('Id') === idUS2AIMeasureSource;
                });
                return source ?? null;
            }),
        );
    }

    // #endregion

    // #region Public methods

    initDefaultValues(model: JsonObject, autoNormalValues: boolean) {
        if (autoNormalValues && isJsonObject(model) && isJsonObject(model.Report) && model.Report.Id == null) {
            setTimeout(() => this.store.dispatch(cpApplyNormalValues({ notifySuccess: true })));
        }

        const existingReferringPhysician = this.modelService.getValue(HardcodedData.bindings.referringPhysician);
        if (!existingReferringPhysician) {
            this.applyUserReferringPhysician();
        }
    }

    isModified() {
        return this.modelService.isModified();
    }

    hasAddendumAnomaly() {
        return this.isInAddendumAnomalyStatus(this.addendums);
    }

    approve(options?: ApproveOptions): Observable<Json> {
        this.waitingService.isWaiting = true;
        const report = this.modelService.getAsJson()?.Report;
        const referringPhysician = options?.referringPhysician
            ? options?.referringPhysician
            : report?.ReferenceExamination?.IdReferringPhysician;

        const referringPhysicians: IReferringPhysician[] = report?.ReferenceExamination?.IdReferringPhysicians;

        if (!report || (!referringPhysician && referringPhysicians?.length <= 1)) {
            return throwError(() => new Error('Cannot approve - missing referring physician.'));
        }

        // TODO: (34.12) Unificare questo if con il caso sotto.
        if (referringPhysicians?.length > 1) {
            return from(
                this.reportingDataBuilder.getReportWorkflowService().approveMulti(
                    this.reportingDataBuilder.getExamService(),
                    this.getReportForApproveOrSign(report),
                    referringPhysicians.map((physician) => physician.Id),
                    false,
                ),
            ).pipe(
                catchError((err: Error) => of(err)),
                switchMap((error) => {
                    this.waitingService.isWaiting = true;
                    // Recuperiamo il nuovo modello anche in caso di errore, perché potrebbe esserci stato un salvataggio.
                    return this.reportingDataBuilder
                        .getMasterDataResolver()
                        .getData()
                        .pipe(
                            switchMap((model) => {
                                // Se presente un errore, lo propaghiamo assieme al nuovo modello.
                                if (error && error instanceof CancellationError) {
                                    error.payload = model;
                                    return throwError(() => error);
                                } else if (error) {
                                    return throwError(() => new PartialError(model, error));
                                }
                                return of(model);
                            }),
                        );
                }),
                finalize(() => (this.waitingService.isWaiting = false)),
            );
        }

        return from(
            this.reportingDataBuilder
                .getReportWorkflowService()
                .approve(
                    this.reportingDataBuilder.getExamService(),
                    this.getReportForApproveOrSign(report),
                    referringPhysician.Id,
                    false,
                    options?.addendum,
                    false,
                ),
        ).pipe(
            map(() => undefined),
            catchError((err: Error) => of(err)),
            switchMap((error) => {
                this.waitingService.isWaiting = true;
                // Recuperiamo il nuovo modello anche in caso di errore, perché potrebbe esserci stato un salvataggio.
                return this.reportingDataBuilder
                    .getMasterDataResolver()
                    .getData()
                    .pipe(
                        switchMap((model) => {
                            // Se presente un errore, lo propaghiamo assieme al nuovo modello.
                            if (error && error instanceof CancellationError) {
                                error.payload = model;
                                return throwError(() => error);
                            } else if (error) {
                                return throwError(() => new PartialError(model, error));
                            }
                            return of(model);
                        }),
                    );
            }),
            finalize(() => (this.waitingService.isWaiting = false)),
        );
    }

    save(forcedReload: boolean) {
        // Se non è specificato il forcedReload, ignoriamo gli errori perché in caso il salvataggio non vada a buon fine
        //   verrà riprovato, ed in quel caso li mostriamo.
        return this.saveWithReload({ forcedReload, ignoreErrors: !forcedReload }).pipe(
            catchError((err) => {
                // Se non è stato chiesto il forcedReload, il client potrebbe non sapere che il flag è necessario,
                //   quindi, nel caso ci sia un errore di merge, riprova il salvataggio con il flag impostato.
                if (!forcedReload && isMergeFault(err?.error)) {
                    return this.saveWithReload({ forcedReload: true, ignoreErrors: false });
                }
                return throwError(() => err);
            }),
        );
    }

    saveWithReload(options: CpHttpSaveOptions): Observable<Json> {
        return this.store.pipe(
            select(selectCpPage),
            take(1),
            map((page) => {
                if (!page) {
                    throw new Error('Salvare i dati. Pagina non aperta.');
                }

                const data = this.modelService.getAsJson();

                data.Report.IsValid = this.modelService.isValid();
                data.Report.IdCustomPage = page.idCustomPage;
                data.Report.IdCustomPageRoot = page.idCustomPageRoot;

                return data;
            }),
            switchMap((data) => {
                this.waitingService.isWaiting = true;

                return this.cpHttpService
                    .save(data, options)
                    .pipe(tapAlways(() => (this.waitingService.isWaiting = false)));
            }),
        );
    }

    sign(options?: SignOptions) {
        const jsonReport = this.modelService.getAsJson().Report;
        const selectedReports = this.getReportForApproveOrSign(jsonReport);
        const referringPhysician = options?.referringPhysician
            ? options?.referringPhysician
            : jsonReport.ReferenceExamination.IdReferringPhysician;
        const user = {
            id: referringPhysician.Id,
            name: referringPhysician.UserName,
        };

        /*il patient report pair ci sara sempre anche nel multi firma*/
        const patientReportPair: IPatientReportPair[] = jsonReport.Examinations.map((e: any) => {
            return {
                patientId: jsonReport.Patient.Id,
                idReportList: [jsonReport.Id],
                isLocalPatient: jsonReport.Patient.IsLocalPatient,
                idExamination: e.Id,
            };
        });

        const examService = this.reportingDataBuilder.getExamService();

        // TODO: (34.12) Unificare questo if con il caso sotto.
        const referringPhysicians: IReferringPhysician[] = jsonReport.ReferenceExamination?.IdReferringPhysicians;
        if (referringPhysicians.length > 1) {
            const multiSignPromise = this.reportingDataBuilder
                .getReportWorkflowService()
                .buildMultiToOneDataSign(
                    selectedReports[0],
                    referringPhysicians.map((rp) => ({ id: rp.Id, name: rp.Name })),
                )
                .then(async (data) => {
                    if (!data) {
                        throw new CancellationError();
                    }

                    if (!options?.addendum && !jsonReport.ReferenceExamination.IsApproved) {
                        await firstValueFrom(this.save(false));
                    }

                    return data;
                })
                .then((data) => this.reportingDataBuilder.getReportWorkflowService().multiSign(examService, data))
                .catch((err: Error) => err)
                .then((error) =>
                    // Recuperiamo il nuovo modello anche in caso di errore, perch� potrebbe esserci stato un salvataggio.
                    firstValueFrom(
                        this.reportingDataBuilder
                            .getMasterDataResolver()
                            .getData()
                            .pipe(
                                switchMap((model) => {
                                    // Se presente un errore, lo propaghiamo assieme al nuovo modello.
                                    if (error) {
                                        return throwError(() => new PartialError(model, error));
                                    }
                                    return of(model);
                                }),
                            ),
                    ),
                );
            return from(multiSignPromise);
        }

        const savePromise = this.reportingDataBuilder
            .getReportWorkflowService()
            .buildDataSign(examService, selectedReports, user, patientReportPair)
            .then(async (data) => {
                if (!data) {
                    throw new CancellationError();
                }
                if (!options?.addendum && !jsonReport.ReferenceExamination.IsApproved) {
                    await firstValueFrom(this.save(false));
                }

                return data;
            })
            .then((data) => {
                return this.reportingDataBuilder
                    .getReportWorkflowService()
                    .sign(examService, data, { print: options?.print, isDeferred: false, addendum: options?.addendum });
            })
            .catch((err: Error) => {
                return err;
            })
            .then((error) =>
                // Recuperiamo il nuovo modello anche in caso di errore, perch� potrebbe esserci stato un salvataggio.
                firstValueFrom(
                    this.reportingDataBuilder
                        .getMasterDataResolver()
                        .getData()
                        .pipe(
                            switchMap((model) => {
                                // Se presente un errore, lo propaghiamo assieme al nuovo modello.
                                if (error) {
                                    return throwError(() => new PartialError(model, error));
                                }
                                return of(model);
                            }),
                        ),
                ),
            );

        return from(savePromise);
    }

    undoValidation() {
        const jsonReport = this.modelService.getAsJson().Report;
        const selectedReports = this.getReportForApproveOrSign(jsonReport);
        const referringPhysician = jsonReport.ReferenceExamination.IdReferringPhysician;
        const reportId = selectedReports[0].id;

        const referringPhysicians: IReferringPhysician[] = jsonReport?.ReferenceExamination?.IdReferringPhysicians;
        // TODO: (34.12) Unificare questo if con il caso sotto.
        if (referringPhysicians?.length > 1) {
            return from(
                this.reportingDataBuilder.getReportWorkflowService().undoMultiValidation(
                    this.reportingDataBuilder.getExamService(),
                    selectedReports,
                    referringPhysicians.map((physician) => physician.Id),
                    false,
                ),
            ).pipe(
                switchMap(() => {
                    this.waitingService.isWaiting = true;
                    return this.reportingDataBuilder.getMasterDataResolver().getData();
                }),
                finalize(() => {
                    this.waitingService.isWaiting = false;
                }),
            );
        }

        return from(
            this.reportingDataBuilder
                .getReportWorkflowService()
                .undoValidation(
                    this.reportingDataBuilder.getExamService(),
                    selectedReports,
                    referringPhysician.Id,
                    reportId,
                    false,
                ),
        ).pipe(
            switchMap(() => {
                this.waitingService.isWaiting = true;
                return this.reportingDataBuilder.getMasterDataResolver().getData();
            }),
            finalize(() => {
                this.waitingService.isWaiting = false;
            }),
        );
    }

    burnCd() {
        const examService = this.reportingDataBuilder.getExamService();
        const selectedReports = this.modelService.getAsJson().Report;
        let burnData = selectedReports?.Examinations.map((exam: any) => {
            return { id: exam.Id };
        }); // , publicationAuthorization: exam.publicationAuthorization
        this.reportingDataBuilder.getReportWorkflowService().burnCd(examService, burnData);
    }

    // #endregion

    //#region Imaging

    addKeyImage(image: KeyImage) {
        let keyImages = this.modelService.getEntity(HardcodedData.bindings.keyImages);
        if (!(keyImages instanceof CpEntityList)) {
            this.logger.error(
                `Report's key images are not an array. IdReport: {${
                    this.modelService.getEntity(HardcodedData.bindings.report)?.Id
                }}`,
            );
            keyImages = this.modelService.makeEntityFromDataset(HardcodedData.bindings.keyImages) as CpEntityList;
        }

        keyImages.addEntityToList(
            new DataBinding(),
            this.modelService.makeEntity<CpEntity>({
                ...image,
                IdExamination: this.referenceExaminationId ?? '',
            }),
        );

        this.modelService.setValue(HardcodedData.bindings.keyImages, keyImages, { force: true });
    }

    deleteKeyImages(keyImagesToRemove: KeyImage[]) {
        const keyImages = this.modelService.getEntities(HardcodedData.bindings.keyImages);

        const keyImagesToKeep = differenceWith(
            keyImages,
            keyImagesToRemove,
            (a, b) => a.getPropertyValue('SOPInstanceUID') === b.SOPInstanceUID,
        );

        this.modelService.setValue(HardcodedData.bindings.keyImages, keyImagesToKeep, { force: true });
    }

    setKeyImages(keyImages: KeyImage[]) {
        keyImages = keyImages.map((img) => ({ ...img, IdExamination: this.referenceExaminationId ?? '' }));

        const entityList = this.modelService.makeEntity(
            keyImages as unknown as JsonObject[],
            HardcodedData.bindings.keyImages,
        );

        this.modelService.setValue(HardcodedData.bindings.keyImages, entityList, { force: true });
    }

    setImagesPerRowInPlaceholder(imagesPerRow: number) {
        this.exam?.setPropertyValue('ImagePerRowInPlaceholder', imagesPerRow);
    }

    updateKeyImage(newImage: KeyImage) {
        const keyImages = this.modelService.getEntities(HardcodedData.bindings.keyImages) ?? [];

        for (const existingImage of keyImages) {
            if (existingImage.getPropertyValue('RepositoryKey') === newImage.RepositoryKey) {
                existingImage.setPropertyValue('SOPInstanceUID', newImage.SOPInstanceUID);
            }
        }
    }

    importMeasuresFromXml(request: MeasureImportRequest) {
        return this.cpHttpService.importMeasuresFromXml(request);
    }

    importMeasuresFromThirdPartsXml(type: XmlRequestType, xml: string) {
        return this.cpHttpService.importMeasuresFromThirdPartsXml(this.modelService.getAsJson().Report, xml, type);
    }

    importSr(request: SrImportRequest) {
        return this.cpHttpService.importSr(request);
    }

    pacsTupleMatches(viewerTuple: PacsTuple) {
        const currentTuples = this.modelService.getEntities(HardcodedData.bindings.pacsTuple) ?? [];
        for (const tuple of currentTuples) {
            const patient = tuple.getEntity(HardcodedData.bindings.patientSimple, false);
            const study = tuple.getEntity(HardcodedData.bindings.study, false);

            if (
                patient &&
                this.pacsTuplePatientMatches(viewerTuple, patient) &&
                study &&
                this.pacsTupleStudyMatches(viewerTuple, study)
            ) {
                return true;
            }
        }

        return false;
    }

    saveHardCodedData(hardCodedData: ObscuredData) {
        return this.reportingDataBuilder.getExamService().saveHardCodedData({
            hardCodedData,
            idClinicalReport: this.reportId!,
        });
    }

    //#endregion

    private getExaminations(model?: ICpEntity) {
        return model?.getAllEntities(HardcodedData.bindings.examinations);
    }

    private getAddendums(model?: ICpEntity) {
        return model?.getAllEntities(HardcodedData.bindings.addendums);
    }

    private getReportForApproveOrSign(item: any): IReport[] {
        const ex = item.ReferenceExamination;
        const exDate = ex?.ExaminationDate ? ' (' + new Date(ex.ExaminationDate).toLocaleDateString() + ')' : '';

        return [
            {
                id: item.Id,
                isAdmitted: ex.IsAdmitted,
                isReported: ex.IsReported,
                isApproved: ex.IsApproved,
                isDigitallySigned: ex.IsDigitallySigned,
                reportDetailForNotification: `${item.Patient?.LastName ?? ''} ${item.Patient?.FirstName} ${exDate}`,
            },
        ];
    }

    //#region Checks

    /**
     * metodo da modificare quando ci saranno alternative al cardio. (es radio)
     * bastera modificare il tipo di ritorno in caso di examination non cardio
     * @param _examination
     * @private
     */
    private isCardioExam(_examination: ICpEntity): boolean {
        return true;
    }

    private isApprovable(examinations: ICpEntity[]) {
        return every(examinations, (ex) => {
            const canApprove = this.radioSettingsService.canApproveCardio();
            return canApprove && !ex.getPropertyValue('IsApproved') && !ex.getPropertyValue('IsDigitallySigned');
        });
    }

    private isInAddendumAnomalyStatus(addendums: ICpEntity[] | undefined) {
        return some(addendums, (ex) => {
            return ex.getPropertyValue('IsDraft') || ex.getPropertyValue('IsDeleting');
        });
    }

    private isApproved(examinations: ICpEntity[]) {
        return every(examinations, (ex) => {
            return ex.getPropertyValue('IsApproved');
        });
    }

    private isSigned(examinations: ICpEntity[]) {
        return every(examinations, (ex) => {
            return ex.getPropertyValue('IsDigitallySigned');
        });
    }

    private isReported(examinations: ICpEntity[]) {
        return every(examinations, (ex) => ex.getPropertyValue('IsReported'));
    }

    private isSignable(examinations: ICpEntity[]) {
        return every(examinations, (ex) => {
            const canSign = this.radioSettingsService.canSignCardio();
            return (
                canSign &&
                (ex.getPropertyValue('IsReported') || ex.getPropertyValue('IsApproved')) &&
                !ex.getPropertyValue('IsDigitallySigned')
            );
        });
    }

    private isExecutable(examinations: ICpEntity[]) {
        return every(examinations, (ex) => {
            return (
                ex.getPropertyValue('IsAdmitted') &&
                !ex.getPropertyValue('IsReported') &&
                !ex.getPropertyValue('IsApproved') &&
                !ex.getPropertyValue('IsDigitallySigned')
            );
        });
    }

    private isValidationUndoable(examinations: ICpEntity[]) {
        return every(examinations, (ex) => {
            return ex.getPropertyValue('IsApproved') || ex.getPropertyValue('IsDigitallySigned');
        });
    }

    private applyUserReferringPhysician() {
        const currentUser = this.authenticationService.getUserId();
        const matchingUser = this.dataSourceService
            .getById('lsReferringPhysician')
            ?.find((item) => item.Id === currentUser);
        if (matchingUser) {
            this.modelService.setValue(HardcodedData.bindings.referringPhysician, matchingUser as any, {
                emitEvent: false,
            });
        }
    }

    private pacsTuplePatientMatches(viewerTuple: PacsTuple, patient: ICpEntity) {
        return (
            (!viewerTuple.patientBirthDate ||
                this.viewerDateToDicomDate(viewerTuple.patientBirthDate) ===
                    this.viewerDateToDicomDate(patient.getPropertyValue('BirthDate'))) &&
            (!viewerTuple.patientID || viewerTuple.patientID === patient.getPropertyValue('PatientId')) &&
            (!viewerTuple.patientName || viewerTuple.patientName === patient.getPropertyValue('FullName')) &&
            (!viewerTuple.patientSex || viewerTuple.patientSex === patient.getPropertyValue('Sex'))
        );
    }

    private pacsTupleStudyMatches(viewerTuple: PacsTuple, study: ICpEntity) {
        return (
            (!viewerTuple.studyAccessionNumber ||
                viewerTuple.studyAccessionNumber === study.getPropertyValue('AccessionNumber')) &&
            (!viewerTuple.studyDate ||
                this.viewerDateToDicomDate(viewerTuple.studyDate) ===
                    this.viewerDateToDicomDate(study.getPropertyValue('StudyDate'))) &&
            (!viewerTuple.studyInstanceUID ||
                viewerTuple.studyInstanceUID === study.getPropertyValue('StudyInstanceUID'))
        );
    }

    private viewerDateToDicomDate(date: PropertyType) {
        if (!date) {
            return null;
        }

        return date.toString().replace(/\//g, '');
    }

    private keyImageFromEntity(e: ICpEntity) {
        return {
            SOPInstanceUID: e.getPropertyValue('SOPInstanceUID')?.toString() ?? '',
            RepositoryKey: e.getPropertyValue('RepositoryKey')?.toString() ?? '',
            IdExamination: e.getPropertyValue('IdExamination')?.toString() ?? '',
            Caption: e.getPropertyValue('Caption')?.toString() ?? '',
            ExcludedFromReport: !!e.getPropertyValue('ExcludedFromReport'),
            IsDeletable: !!e.getPropertyValue('IsDeletable'),
        };
    }

    //#endregion
}
